import { pt, unitPolygon } from './shapeGeometry';
import type { Box, UnitPoint } from './shapeGeometry';

/** Shapes added with the shape library sidebar (D-038): more basic shapes, flowchart, arrows and callouts. */
export type MoreShapeType =
  | 'pentagon'
  | 'octagon'
  | 'star'
  | 'trapezoid'
  | 'start-end'
  | 'document'
  | 'predefined-process'
  | 'manual-input'
  | 'delay'
  | 'off-page-link'
  | 'arrow'
  | 'double-arrow'
  | 'arrow-up'
  | 'chevron'
  | 'speech-bubble'
  | 'cloud'
  | 'sticky-note'
  | 'heart';

const HALF = 0.5;
const STAR_POINTS = 5;
const STAR_INNER_RADIUS = 0.4;
/** Inner vertical lines of `predefined-process`, as a share of the width from each side. */
const SUBROUTINE_INSET = 0.12;
/** Folded corner of `sticky-note` and corner radius of `speech-bubble`, as a share of the shorter side. */
const FOLD_SHARE = 0.25;
const BUBBLE_CORNER_SHARE = 0.12;

/** A five-point star as unit points, scaled so its tips touch the box edges. */
const STAR: UnitPoint[] = (() => {
  const raw = Array.from({ length: STAR_POINTS * 2 }, (_, index) => {
    const angle = -Math.PI / 2 + (index * Math.PI) / STAR_POINTS;
    const radius = index % 2 === 0 ? 1 : STAR_INNER_RADIUS;
    return [Math.cos(angle) * radius, Math.sin(angle) * radius] as const;
  });
  const xs = raw.map(([x]) => x);
  const ys = raw.map(([, y]) => y);
  const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  return raw.map(([x, y]) => [(x - minX) / (maxX - minX), (y - minY) / (maxY - minY)] as const);
})();

const UNIT_POLYGONS: Partial<Record<MoreShapeType, UnitPoint[]>> = {
  pentagon: [[0.5, 0], [1, 0.38], [0.81, 1], [0.19, 1], [0, 0.38]],
  octagon: [[0.29, 0], [0.71, 0], [1, 0.29], [1, 0.71], [0.71, 1], [0.29, 1], [0, 0.71], [0, 0.29]],
  star: STAR,
  trapezoid: [[0.2, 0], [0.8, 0], [1, 1], [0, 1]],
  'manual-input': [[0, 0.3], [1, 0], [1, 1], [0, 1]],
  'off-page-link': [[0, 0], [1, 0], [1, 0.65], [0.5, 1], [0, 0.65]],
  arrow: [[0, 0.3], [0.6, 0.3], [0.6, 0], [1, 0.5], [0.6, 1], [0.6, 0.7], [0, 0.7]],
  'double-arrow': [[0, 0.5], [0.25, 0], [0.25, 0.3], [0.75, 0.3], [0.75, 0], [1, 0.5], [0.75, 1], [0.75, 0.7], [0.25, 0.7], [0.25, 1]],
  'arrow-up': [[0.5, 0], [1, 0.4], [0.7, 0.4], [0.7, 1], [0.3, 1], [0.3, 0.4], [0, 0.4]],
  chevron: [[0, 0], [0.75, 0], [1, 0.5], [0.75, 1], [0, 1], [0.25, 0.5]],
};

const startEnd = ({ x, y, width, height }: Box): string => {
  const r = Math.min(height, width) * HALF;
  return `M${x + r} ${y}H${x + width - r}A${r} ${r} 0 0 1 ${x + width - r} ${y + height}H${x + r}A${r} ${r} 0 0 1 ${x + r} ${y}Z`;
};

const documentShape = (box: Box): string =>
  `M${pt(box, [0, 0])}L${pt(box, [1, 0])}L${pt(box, [1, 0.86])}C${pt(box, [0.8, 1.02])} ${pt(box, [0.68, 0.7])} ${pt(box, [0.5, 0.86])}`
  + `S${pt(box, [0.2, 1.02])} ${pt(box, [0, 0.86])}Z`;

const predefinedProcess = (box: Box): string =>
  `${unitPolygon(box, [[0, 0], [1, 0], [1, 1], [0, 1]])}`
  + `M${pt(box, [SUBROUTINE_INSET, 0])}L${pt(box, [SUBROUTINE_INSET, 1])}`
  + `M${pt(box, [1 - SUBROUTINE_INSET, 0])}L${pt(box, [1 - SUBROUTINE_INSET, 1])}`;

const delay = ({ x, y, width, height }: Box): string => {
  const rx = Math.min(width * HALF, height * HALF);
  const ry = height * HALF;
  return `M${x} ${y}H${x + width - rx}A${rx} ${ry} 0 0 1 ${x + width - rx} ${y + height}H${x}Z`;
};

const speechBubble = ({ x, y, width, height }: Box): string => {
  const r = Math.min(width, height) * BUBBLE_CORNER_SHARE;
  const right = x + width;
  const bottom = y + height * 0.8;
  return `M${x + r} ${y}H${right - r}Q${right} ${y} ${right} ${y + r}V${bottom - r}Q${right} ${bottom} ${right - r} ${bottom}`
    + `H${x + width * 0.4}L${x + width * 0.2} ${y + height}V${bottom}H${x + r}Q${x} ${bottom} ${x} ${bottom - r}`
    + `V${y + r}Q${x} ${y} ${x + r} ${y}Z`;
};

const cloud = (box: Box): string => {
  const { width, height } = box;
  const arc = (rx: number, ry: number, to: UnitPoint) => `A${rx * width} ${ry * height} 0 0 1 ${pt(box, to)}`;
  return `M${pt(box, [0.22, 0.95])}${arc(0.2, 0.22, [0.17, 0.5])}${arc(0.2, 0.25, [0.5, 0.2])}`
    + `${arc(0.2, 0.22, [0.85, 0.47])}${arc(0.18, 0.24, [0.8, 0.95])}Z`;
};

const stickyNote = (box: Box): string => {
  const fold = Math.min(box.width, box.height) * FOLD_SHARE;
  const foldU = fold / box.width;
  const foldV = fold / box.height;
  return `${unitPolygon(box, [[0, 0], [1, 0], [1, 1 - foldV], [1 - foldU, 1], [0, 1]])}`
    + `M${pt(box, [1 - foldU, 1])}L${pt(box, [1 - foldU, 1 - foldV])}L${pt(box, [1, 1 - foldV])}`;
};

const heart = (box: Box): string =>
  `M${pt(box, [0.5, 1])}C${pt(box, [0.2, 0.8])} ${pt(box, [0, 0.6])} ${pt(box, [0, 0.33])}`
  + `C${pt(box, [0, 0.1])} ${pt(box, [0.2, 0])} ${pt(box, [0.3, 0])}C${pt(box, [0.4, 0])} ${pt(box, [0.48, 0.07])} ${pt(box, [0.5, 0.18])}`
  + `C${pt(box, [0.52, 0.07])} ${pt(box, [0.6, 0])} ${pt(box, [0.7, 0])}C${pt(box, [0.8, 0])} ${pt(box, [1, 0.1])} ${pt(box, [1, 0.33])}`
  + `C${pt(box, [1, 0.6])} ${pt(box, [0.8, 0.8])} ${pt(box, [0.5, 1])}Z`;

const fromUnitPolygon = (type: MoreShapeType) => (box: Box): string => unitPolygon(box, UNIT_POLYGONS[type] ?? []);

/** Outline builders for the shapes of the library sidebar. */
export const MORE_SHAPE_BUILDERS: Record<MoreShapeType, (box: Box) => string> = {
  pentagon: fromUnitPolygon('pentagon'),
  octagon: fromUnitPolygon('octagon'),
  star: fromUnitPolygon('star'),
  trapezoid: fromUnitPolygon('trapezoid'),
  'manual-input': fromUnitPolygon('manual-input'),
  'off-page-link': fromUnitPolygon('off-page-link'),
  arrow: fromUnitPolygon('arrow'),
  'double-arrow': fromUnitPolygon('double-arrow'),
  'arrow-up': fromUnitPolygon('arrow-up'),
  chevron: fromUnitPolygon('chevron'),
  'start-end': startEnd,
  document: documentShape,
  'predefined-process': predefinedProcess,
  delay,
  'speech-bubble': speechBubble,
  cloud,
  'sticky-note': stickyNote,
  heart,
};
